import { useEffect, useState } from "react";
import { api, ApiClientError } from "./lib/api";
import type { MeResponse } from "./lib/types";
import { useTheme } from "./lib/useTheme";
import Dashboard from "./pages/Dashboard";
import LegalPage from "./pages/Legal";
import Login from "./pages/Login";

type AuthState =
  | { status: "loading" }
  | { status: "anonymous"; reason?: string }
  | { status: "authenticated"; me: Extract<MeResponse, { authenticated: true }> }
  | { status: "error"; message: string };

export default function App() {
  const theme = useTheme();
  const [auth, setAuth] = useState<AuthState>({ status: "loading" });

  useEffect(() => {
    let active = true;
    api
      .me()
      .then((me) => {
        if (!active) return;
        if (me.authenticated) setAuth({ status: "authenticated", me });
        else setAuth({ status: "anonymous", reason: me.reason });
      })
      .catch((e: unknown) => {
        if (!active) return;
        if (e instanceof ApiClientError && e.status === 401) {
          setAuth({ status: "anonymous" });
        } else {
          setAuth({
            status: "error",
            message: e instanceof Error ? e.message : String(e),
          });
        }
      });
    return () => {
      active = false;
    };
  }, []);

  const urlParams =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search)
      : new URLSearchParams();
  const loginError = urlParams.get("error");
  const pathname = typeof window !== "undefined" ? window.location.pathname : "/";

  if (pathname === "/privacy") {
    return <LegalPage page="privacy" theme={theme} />;
  }

  if (pathname === "/terms") {
    return <LegalPage page="terms" theme={theme} />;
  }

  if (auth.status === "loading") {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <div className="h-6 w-6 animate-pulse rounded-full bg-zinc-200 dark:bg-zinc-800" />
      </div>
    );
  }

  if (auth.status === "error") {
    return (
      <div className="mx-auto flex min-h-dvh max-w-md items-center px-6">
        <div className="card w-full p-6">
          <h1 className="text-lg font-semibold">Something went wrong</h1>
          <p className="mt-2 text-sm text-zinc-500">{auth.message}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="btn-primary mt-4"
          >
            Reload
          </button>
        </div>
      </div>
    );
  }

  if (auth.status === "anonymous") {
    return <Login error={loginError ?? auth.reason ?? null} theme={theme} />;
  }

  return <Dashboard me={auth.me} theme={theme} />;
}
